// feedFilterActions.js
'use strict';
import { LIVESTREAM_FEED_SORTINGS_V2 } from '../RemoteConfigKeys.js';

import getOperationData from '../selector/getOperationData.js';

import { fetchFeedsTotalCountCore as fetchFeedsTotalCount } from '../action/fetchFeedsTotalCount.js';
import { replace } from '../action/navigationAction.js';

import { QUERY_SEPARATOR } from '../resource/feedConstants.js';
import { parseOptionsV2 } from '../resource/feedUtils.js';

import { SET_OPERATION_DATA } from '../ActionTypes.js';

/**
 * Set initial selected options
 * basically this action is used to set initial selected options from url
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @param {string} {selectedOptions} - selected options.
 * @return {Promise} Action promise.
 */
export const initSelectedOptions =
  ({ baseFeedName = '', selectedOptions = [] }) =>
  dispatch => {
    return dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'selectedOptions'],
        data: selectedOptions,
      },
    });
  };

/**
 * Set selected options
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @param {array} {selectedOptions} - selected options.
 * @return {Promise} Action promise.
 */
export const setSelectedOptions =
  ({ baseFeedName = '', selectedOptions = [] }) =>
  dispatch => {
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'selectedOptions'],
        data: selectedOptions,
      },
    });

    return dispatch(setURL({ baseFeedName, options: selectedOptions }));
  };

/**
 * Set selected option
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @param {string} {option} - option.
 * @return {Promise} Action promise.
 */
export const setSelectedOption =
  ({ baseFeedName = '', groupOption = '', isSingleSelection = false }) =>
  (dispatch, getState) => {
    const [group] = groupOption.split('/');

    const selectedOptions =
      getOperationData(
        getState(),
        ['feed', baseFeedName, 'filter_v2'],
        'selectedOptions'
      ) || [];

    // Initialize filtered options based on whether it's a single selection mode
    let filteredOptions = isSingleSelection
      ? selectedOptions.filter(option => !option.startsWith(group))
      : [...selectedOptions];

    // Toggle the target option (add if not present, remove if present)
    const updatedSelectedOptions = filteredOptions.includes(groupOption)
      ? filteredOptions.filter(selectedOption => selectedOption !== groupOption)
      : [...filteredOptions, groupOption];

    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'selectedOptions'],
        data: updatedSelectedOptions,
      },
    });

    return dispatch(
      setURL({
        baseFeedName,
        options: updatedSelectedOptions,
      })
    );
  };

/**
 * Reset selected options
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @return {Promise} Action promise.
 */
export const resetSelectedOptions =
  ({ baseFeedName = '' }) =>
  (dispatch, getState) => {
    const selectedOptions =
      getOperationData(
        getState(),
        ['feed', baseFeedName, 'filter_v2'],
        'selectedOptions'
      ) || [];

    // Keep only the sortings options
    const updatedSelectedOptions = selectedOptions.filter(selectedOption =>
      selectedOption.startsWith(LIVESTREAM_FEED_SORTINGS_V2)
    );

    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'selectedOptions'],
        data: updatedSelectedOptions,
      },
    });

    return dispatch(setURL({ baseFeedName, options: updatedSelectedOptions }));
  };

/**
 * Set initial draft options
 * basically this action is used to set initial draft options for FeedFilterModal
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @param {array} {draftOptions} - draft options.
 * @return {Promise} Action promise.
 */
export const initDraftOptions =
  ({ baseFeedName = '', draftOptions = [] }) =>
  dispatch => {
    return dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'draftOptions'],
        data: draftOptions,
      },
    });
  };

/**
 * Set draft option
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @param {string} {groupOption} - group option.
 * @return {Promise} Action promise.
 */
export const setDraftOption =
  ({ baseFeedName = '', groupOption = '' }) =>
  (dispatch, getState) => {
    const draftOptions =
      getOperationData(
        getState(),
        ['feed', baseFeedName, 'filter_v2'],
        'draftOptions'
      ) || [];

    // Toggle the target option (add if not present, remove if present)
    const updatedDraftOptions = draftOptions.includes(groupOption)
      ? draftOptions.filter(draftOption => draftOption !== groupOption)
      : [...draftOptions, groupOption];

    return dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'draftOptions'],
        data: updatedDraftOptions,
      },
    });
  };

/**
 * Reset draft options
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @return {Promise} Action promise.
 */
export const resetDraftOptions =
  ({ baseFeedName = '' }) =>
  (dispatch, getState) => {
    const draftOptions =
      getOperationData(
        getState(),
        ['feed', baseFeedName, 'filter_v2'],
        'draftOptions'
      ) || [];

    // Keep only the sortings options
    const updatedDraftOptions = draftOptions.filter(draftOption =>
      draftOption.startsWith(LIVESTREAM_FEED_SORTINGS_V2)
    );

    return dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['feed', baseFeedName, 'filter_v2', 'draftOptions'],
        data: updatedDraftOptions,
      },
    });
  };

/**
 * Set URL with options
 * @kind action
 * @param {array} {options} - options.
 * @return {Promise} Action promise.
 */
const setURL =
  ({ baseFeedName = '', options = [] }) =>
  dispatch => {
    const {
      intersectedFeedNames = [],
      filters = [],
      sorts = [],
    } = parseOptionsV2({
      baseFeedName,
      options,
    });

    const url = new URL(window.location.href);
    if (
      intersectedFeedNames.length &&
      (filters.length > 0 || sorts.length > 0)
    ) {
      url.searchParams.set('feed', intersectedFeedNames.join(QUERY_SEPARATOR));
    } else {
      ['feed', '_feed'].forEach(key => url.searchParams.delete(key));
    }
    if (filters.length) {
      url.searchParams.set('filters', filters.join(QUERY_SEPARATOR));
    } else {
      ['filters', '_filters'].forEach(key => url.searchParams.delete(key));
    }
    if (sorts.length) {
      url.searchParams.set('sorts', sorts.join(QUERY_SEPARATOR));
    } else {
      ['sorts', '_sorts'].forEach(key => url.searchParams.delete(key));
    }

    dispatch(
      replace({
        pathname: url.pathname,
        search: url.search,
      })
    );
  };

/**
 * Fetch feeds total count v2
 * @kind action
 * @param {string} {baseFeedName} - base feed name.
 * @return {Promise} Action promise.
 */
export const fetchFeedsTotalCountV2 =
  ({ baseFeedName = '' }) =>
  (dispatch, getState) => {
    const draftOptions =
      getOperationData(
        getState(),
        ['feed', baseFeedName, 'filter_v2'],
        'draftOptions'
      ) || [];

    const { feedNameWithQuery } = parseOptionsV2({
      baseFeedName,
      options: draftOptions,
    });

    return dispatch(
      fetchFeedsTotalCount({
        type: feedNameWithQuery,
        shouldSendTrackingEvent: true,
      })
    );
  };
